import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Spinner, Row, Col, Container } from "reactstrap";
import useCustomFecthQuery from "../../../Hooks/useCustomFecthQuery";
import SurveyForm from "./SurveyForm";
import SurveyQRCode from "./SurveyQRCode";
import SurveyAnalytics from "./SurveyAnalytics";
import { FaEdit, FaQrcode, FaChartBar, FaTrash, FaPlus, FaCheckCircle, FaClock, FaUsers, FaEye } from "react-icons/fa";
import { dangerMessage, successMessage } from "../../Toast/Toast";
import { deleteSurvey } from "../../../helpers/surveyApi";

// Error boundary wrapper
class SurveyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // console.error("❌ Survey component error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "24px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
          <Card style={{ border: "none", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <CardBody style={{ padding: "32px", textAlign: "center" }}>
              <div style={{ 
                width: "64px", 
                height: "64px", 
                background: "#fef2f2", 
                borderRadius: "50%", 
                margin: "0 auto 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ef4444"
              }}>
                <FaTrash size={24} />
              </div>
              <h4 style={{ margin: "0 0 12px", color: "#1f2937", fontWeight: "600" }}>Error Loading Survey</h4>
              <p style={{ margin: "0 0 20px", color: "#6b7280", fontSize: "14px" }}>
                {this.state.error?.message || "An unexpected error occurred"}
              </p>
              <Button 
                color="primary" 
                onClick={() => this.setState({ hasError: false, error: null })}
                style={{ 
                  background: "#f97316", 
                  border: "none", 
                  borderRadius: "8px",
                  padding: "10px 24px",
                  fontWeight: "500"
                }}
              >
                Try Again
              </Button>
            </CardBody>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

const Survey = ({ eventDetails, token, userID, isLoading }) => {
  const [showForm, setShowForm] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);

  const eventId = eventDetails?._id;

  useEffect(() => {
    return () => {
      // Cleanup if needed
    };
  }, [eventId, eventDetails]);

  // Fetch survey for this event
  const {
    data: surveyData,
    isLoading: isLoadingSurvey,
    error: surveyError,
    refetch: refetchSurvey,
  } = useCustomFecthQuery(
    ["survey", eventId],
    `${process.env.REACT_APP_API}surveys/event/${eventId}`,
    token,
    { enabled: !!eventId }
  );

  const survey = surveyData?.data || null;

  const handleCreateSurvey = () => {
    setEditingSurvey(null);
    setShowForm(true);
    setShowQRCode(false);
    setShowAnalytics(false);
  };

  const handleEditSurvey = () => {
    setEditingSurvey(survey);
    setShowForm(true);
    setShowQRCode(false);
    setShowAnalytics(false);
  };

  const handleViewQRCode = () => {
    setShowQRCode(true);
    setShowForm(false);
    setShowAnalytics(false);
  };

  const handleViewAnalytics = () => {
    setShowAnalytics(true);
    setShowForm(false);
    setShowQRCode(false);
  };

  const handleDeleteSurvey = async () => {
    if (!window.confirm("Are you sure you want to delete this survey? This action cannot be undone.")) {
      return;
    }

    try {
      const result = await deleteSurvey(survey._id, token);
      if (result?.status === "success") {
        successMessage("Survey deleted successfully");
        refetchSurvey();
      } else {
        dangerMessage(result?.message || "Failed to delete survey");
      }
    } catch (error) {
      dangerMessage("Error deleting survey");
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingSurvey(null);
    refetchSurvey();
  };

  const handleQRCodeClose = () => {
    setShowQRCode(false);
  };

  const handleAnalyticsClose = () => {
    setShowAnalytics(false);
  };

  // Shared styles matching SurveyForm/Analytics
  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "24px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
    },
    header: {
      marginBottom: "32px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      flexWrap: "wrap",
      gap: "16px",
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#1f2937",
      margin: "0 0 8px 0",
    },
    subtitle: {
      fontSize: "14px",
      color: "#6b7280",
      margin: 0,
    },
    card: {
      border: "none",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      marginBottom: "24px",
      overflow: "hidden",
      background: "white",
    },
    cardHeader: {
      background: "white",
      borderBottom: "2px solid #f3f4f6",
      padding: "20px 24px",
      fontWeight: "600",
      fontSize: "1.1rem",
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
    },
    cardBody: {
      padding: "24px",
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "600",
    },
    statusActive: {
      backgroundColor: "#ecfdf3",
      color: "#16a34a",
      border: "1px solid #bbf7d0",
    },
    statusInactive: {
      backgroundColor: "#f3f4f6",
      color: "#6b7280",
      border: "1px solid #e5e7eb",
    },
    actionButton: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "14px 20px",
      borderRadius: "10px",
      border: "none",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s",
      width: "100%",
      minHeight: "56px",
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
      color: "white",
    },
    btnInfo: {
      background: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)",
      color: "white",
    },
    btnSuccess: {
      background: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
      color: "white",
    },
    btnDanger: {
      background: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
      color: "white",
    },
    btnSecondary: {
      background: "white",
      color: "#374151",
      border: "1px solid #d1d5db",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 24px",
      background: "#f9fafb",
      borderRadius: "12px",
      border: "1px dashed #e5e7eb",
    },
    surveyInfo: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    infoRow: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "14px",
      color: "#6b7280",
    },
    infoIcon: {
      color: "#f97316",
      fontSize: "16px",
    },
  };

  // Don't show loading if eventId is not available
  if (!eventId) {
    return (
      <div style={styles.container}>
        <Card style={styles.card}>
          <CardBody style={{ ...styles.cardBody, textAlign: "center" }}>
            <Spinner color="primary" />
            <p style={{ marginTop: "16px", color: "#6b7280", fontSize: "14px" }}>
              Loading event information...
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (isLoading || isLoadingSurvey) {
    return (
      <div style={styles.container}>
        <Card style={styles.card}>
          <CardBody style={{ ...styles.cardBody, textAlign: "center", padding: "48px" }}>
            <Spinner color="primary" size="lg" />
            <p style={{ marginTop: "20px", color: "#6b7280", fontSize: "14px" }}>
              Loading survey data...
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Show form if creating or editing
  if (showForm) {
    return (
      <SurveyForm
        eventId={eventId}
        survey={editingSurvey}
        token={token}
        onClose={handleFormClose}
        onSuccess={handleFormClose}
      />
    );
  }

  // Show QR Code view
  if (showQRCode && survey) {
    return (
      <SurveyQRCode
        surveyId={survey._id}
        surveyTitle={survey.title}
        token={token}
        onClose={handleQRCodeClose}
      />
    );
  }

  // Show Analytics view
  if (showAnalytics && survey) {
    return (
      <SurveyAnalytics
        surveyId={survey._id}
        token={token}
        onClose={handleAnalyticsClose}
      />
    );
  }

  // Main survey section view
  return (
    <div style={styles.container}>
      {/* Custom CSS for responsive enhancements */}
      <style>{`
        @media (max-width: 768px) {
          .survey-header-mobile {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .action-grid-mobile .col-12 {
            margin-bottom: 12px;
          }
          .action-grid-mobile .col-12:last-child {
            margin-bottom: 0;
          }
        }
        .action-btn-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.12) !important;
        }
      `}</style>

      {/* Header */}
      <div style={{ ...styles.header, className: "survey-header-mobile" }}>
        <div>
          <h1 style={styles.title}>Survey Management</h1>
          <p style={styles.subtitle}>Create, manage, and analyze feedback for your event</p>
        </div>
      </div>

      {/* Error Banner */}
      {(surveyError || (surveyData && surveyData.error)) && (
        <Card style={{ ...styles.card, borderLeft: "4px solid #f59e0b", marginBottom: "24px" }}>
          <CardBody style={{ padding: "16px 24px", display: "flex", alignItems: "center", gap: "12px" }}>
            <FaClock style={{ color: "#f59e0b", fontSize: "18px" }} />
            <div>
              <strong style={{ color: "#1f2937", fontSize: "14px" }}>Unable to load survey data</strong>
              <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "13px" }}>
                You can still create a new survey or try refreshing the page.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {!survey ? (
        // Empty State - No Survey Created
        <Card style={styles.card}>
          <CardBody style={{ ...styles.cardBody, textAlign: "center" }}>
           <div style={styles.emptyState}>
  <div style={{ 
    width: "72px", 
    height: "72px", 
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", 
    borderRadius: "50%", 
    margin: "0 auto 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#3b82f6"
  }}>
    <FaPlus size={28} />
  </div>
  <h3 style={{ margin: "0 0 12px", color: "#1f2937", fontWeight: "600", fontSize: "20px" }}>
    No Survey Created Yet
  </h3>
  <p style={{ margin: "0 0 24px", color: "#6b7280", fontSize: "14px", maxWidth: "400px", marginInline: "auto" }}>
    Create your first survey to start collecting valuable feedback from your event attendees.
  </p>
  <div style={{ display: "flex", justifyContent: "center" }}>
    <Button 
      onClick={handleCreateSurvey}
      style={{ 
        ...styles.actionButton, 
        ...styles.btnPrimary,
        width: "auto",
        padding: "12px 32px",
        borderRadius: "10px"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 16px rgba(249, 115, 22, 0.3)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <FaPlus style={{ marginRight: "8px" }} />
      Create Your First Survey
    </Button>
  </div>
</div>
          </CardBody>
        </Card>
      ) : (
        // Survey Exists - Show Details & Actions
        <>
          {/* Survey Info Card */}
          <Card style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaChartBar style={{ color: "#f97316", fontSize: "20px" }} />
                <div>
                  <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>
                    {survey.title}
                  </h2>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                    <span style={{ 
                      ...styles.statusBadge, 
                      ...(survey.isActive ? styles.statusActive : styles.statusInactive) 
                    }}>
                      {survey.isActive ? <FaCheckCircle size={12} /> : <FaClock size={12} />}
                      {survey.isActive ? "Active" : "Inactive"}
                    </span>
                    <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                      {survey.questions?.length || 0} questions
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <CardBody style={styles.cardBody}>
              {survey.description && (
                <p style={{ 
                  margin: "0 0 24px", 
                  color: "#4b5563", 
                  fontSize: "14px", 
                  lineHeight: "1.6",
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb"
                }}>
                  {survey.description}
                </p>
              )}

              {/* Survey Metadata */}
              <div style={styles.surveyInfo}>
                <div style={styles.infoRow}>
                  <FaUsers style={styles.infoIcon} />
                  <span>
                    <strong style={{ color: "#374151" }}>Responses:</strong>{" "}
                    <span style={{ color: "#6b7280" }}>
                      {survey.totalResponses || 0} collected
                    </span>
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <FaEye style={styles.infoIcon} />
                  <span>
                    <strong style={{ color: "#374151" }}>Completion Rate:</strong>{" "}
                    <span style={{ color: "#6b7280" }}>
                      {survey.completionRate ? `${survey.completionRate}%` : "N/A"}
                    </span>
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <FaClock style={styles.infoIcon} />
                  <span>
                    <strong style={{ color: "#374151" }}>Last Updated:</strong>{" "}
                    <span style={{ color: "#6b7280" }}>
                      {survey.updatedAt ? new Date(survey.updatedAt).toLocaleDateString() : "N/A"}
                    </span>
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Action Buttons Grid */}
          <Card style={styles.card}>
            <CardBody style={styles.cardBody}>
              <Row className="g-3 action-grid-mobile">
                <Col xs="12" sm="6" lg="3">
                  <button
                    onClick={handleEditSurvey}
                    style={{ ...styles.actionButton, ...styles.btnPrimary }}
                    className="action-btn-hover"
                  >
                    <FaEdit size={18} />
                    Edit Survey
                  </button>
                </Col>
                <Col xs="12" sm="6" lg="3">
                  <button
                    onClick={handleViewQRCode}
                    style={{ ...styles.actionButton, ...styles.btnInfo }}
                    className="action-btn-hover"
                  >
                    <FaQrcode size={18} />
                    View QR Code
                  </button>
                </Col>
                <Col xs="12" sm="6" lg="3">
                  <button
                    onClick={handleViewAnalytics}
                    style={{ ...styles.actionButton, ...styles.btnSuccess }}
                    className="action-btn-hover"
                  >
                    <FaChartBar size={18} />
                    View Analytics
                  </button>
                </Col>
                <Col xs="12" sm="6" lg="3">
                  <button
                    onClick={handleDeleteSurvey}
                    style={{ ...styles.actionButton, ...styles.btnDanger }}
                    className="action-btn-hover"
                  >
                    <FaTrash size={18} />
                    Delete Survey
                  </button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Quick Tips Card */}
          <Card style={{ ...styles.card, background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)", border: "1px solid #fed7aa" }}>
            <CardBody style={{ padding: "20px 24px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <FaCheckCircle style={{ color: "#f97316", fontSize: "20px", marginTop: "2px", flexShrink: 0 }} />
                <div>
                  <h4 style={{ margin: "0 0 8px", color: "#1f2937", fontWeight: "600", fontSize: "15px" }}>
                    Pro Tip
                  </h4>
                  <p style={{ margin: 0, color: "#6b7280", fontSize: "13px", lineHeight: "1.6" }}>
                    Share your survey QR code at your event for instant feedback collection. 
                    Monitor responses in real-time through the Analytics dashboard.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

// Wrap component in error boundary
const SurveyWithErrorBoundary = (props) => (
  <SurveyErrorBoundary>
    <Survey {...props} />
  </SurveyErrorBoundary>
);

export default SurveyWithErrorBoundary;